import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist';

import { authApi } from '@ports/internal-services/auth/authApi';
import { notificationApi } from '@ports/internal-services/notification/notificationApi';
import { countryApi } from '@ports/internal-services/country/countryApi';
import { fileExportApi } from '@ports/internal-services/fileExport/fileExportApi';
import { stateApi } from '@ports/internal-services/state/stateApi';
import { zipCodeApi } from '@ports/internal-services/zipCodes/zipCodeApi';
import { userApi } from '@ports/internal-services/user/userApi';
import { scheduleApi } from '@ports/internal-services/schedule/scheduleApi';
import { localareaApi } from '@ports/internal-services/localarea/localareaApi';
import { faqApi } from '@ports/internal-services/faq/faqApi';
import { downloadCenterApi } from '@ports/internal-services/downloadCenter/downloadCenterApi';
import { mealRequestApi } from '@ports/internal-services/mealRequest/mealRequestApi';
import { resourceApi } from '@ports/internal-services/resource/resourceApi';
import { manualMatchingApi } from '@ports/internal-services/manualMatching/manualMatchingApi';
import { insightsDashboardApi } from '@ports/internal-services/insightsDashboard/insightsDashboardApi';
import { userDetailsApi } from '@ports/internal-services/userDetails/userDetailsApi';
import { contactListApi } from '@ports/internal-services/contactList/contactListApi';
import { settingsApi } from '@ports/internal-services/settings/settingsApi';
import { captchaApi } from '@ports/internal-services/captcha/captchaApi';
import { groupsApi } from '@ports/internal-services/groups/groupsApi';
import { userRoleRequestApi } from '@ports/internal-services/userRoleRequest/userRoleRequestApi';
import { eventLasagnaApi } from '@ports/internal-services/eventLasagna/eventLasagnaApi';
import { utilizationApi } from '@ports/internal-services/utilization/utilizationApi';
import { homeApi } from '@ports/internal-services/home/homeApi';
import { rootReducer, rootPersistConfig } from './reducer';

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      },
      immutableCheck: false
    })
      .concat(authApi.middleware)
      .concat(notificationApi.middleware)
      .concat(countryApi.middleware)
      .concat(stateApi.middleware)
      .concat(fileExportApi.middleware)
      .concat(zipCodeApi.middleware)
      .concat(userApi.middleware)
      .concat(scheduleApi.middleware)
      .concat(localareaApi.middleware)
      .concat(faqApi.middleware)
      .concat(downloadCenterApi.middleware)
      .concat(mealRequestApi.middleware)
      .concat(resourceApi.middleware)
      .concat(manualMatchingApi.middleware)
      .concat(insightsDashboardApi.middleware)
      .concat(userDetailsApi.middleware)
      .concat(contactListApi.middleware)
      .concat(settingsApi.middleware)
      .concat(captchaApi.middleware)
      .concat(groupsApi.middleware)
      .concat(userRoleRequestApi.middleware)
      .concat(eventLasagnaApi.middleware)
      .concat(utilizationApi.middleware)
      .concat(homeApi.middleware)
});

export const persister = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
